import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import type { ParsedBrand } from "../types";

export interface ImportBrandsParams {
  validBrands: ParsedBrand[];
  organizationId: string;
  onSuccess: () => void;
}

export async function executeBrandImport({
  validBrands,
  organizationId,
  onSuccess,
}: ImportBrandsParams): Promise<boolean> {
  if (!validBrands.length) {
    toast({
      title: "No valid data to import",
      description: "Please fix validation errors first.",
      variant: "destructive",
    });
    return false;
  }

  if (!organizationId) {
    toast({
      title: "Error",
      description: "No organization selected. Please select an organization first.",
      variant: "destructive",
    });
    return false;
  }

  try {
    const brandsWithOrgId = validBrands.map((brand) => ({
      ...brand,
      organization_id: organizationId,
      created_by: null,
    }));

    const { data, error } = await supabase
      .from("brands")
      .insert(brandsWithOrgId)
      .select();

    if (error) {
      console.error("Import error:", error);
      toast({
        title: "Import failed",
        description: error.message || "Failed to import brands.",
        variant: "destructive",
      });
      return false;
    }

    toast({
      title: "Import successful!",
      description: `Successfully imported ${data?.length || validBrands.length} brands.`,
    });

    onSuccess();
    return true;
  } catch (err: unknown) {
    console.error("Import error:", err);
    const message = err instanceof Error ? err.message : "An unexpected error occurred.";
    toast({
      title: "Import failed",
      description: message,
      variant: "destructive",
    });
    return false;
  }
}
